import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import DisabledReason from '../system/DisabledReason'
import AlertSchedulePicker from './AlertSchedulePicker'
import AlertRecipientsField from './AlertRecipientsField'
import type { AlertEventType, AlertRule, AlertSchedule, AlertSource } from '../../types/gotypes'
import type { AlertRecipient, CreateAlertBody, UpdateAlertBody } from '../../hooks/useAlerts'
import type { AlertFormPipeline } from '../../hooks/useAlertForm'
import { ALERT_EVENT_TYPES, ALERT_SOURCE_KINDS, alertSourceOf, isBatchEvent } from '../../utils/alertSources'
import { defaultAlertSchedule, sameSchedule, scheduleBody } from '../../utils/alertSchedule'

export const SAVED_ACTIVE_LINE = 'Saved active: you are notified each time it happens.'

export const KIND_FIXED_LINE = 'The kind is fixed. To watch something else, create a new alert.'

export const CHANGE_WITH_AGENT_LINE =
  'What it watches and when it fires change in a chat with the agent, which rewrites and tests the check.'

const tablePath = (t: { folder_path: string; filename: string }) => (t.folder_path ? `${t.folder_path}/${t.filename}` : t.filename)

const sameEmails = (a: string[], b: string[]) => [...a].sort().join('\n') === [...b].sort().join('\n')

export interface AlertFormJob {
  name: string
  displayName?: string
}

export interface AlertFormDialogProps {
  open: boolean
  /** The alert being edited; absent for a new event alert ("Watch an event"). */
  alert?: AlertRule
  /** The customer's jobs the user can pick from (their session jobs). */
  jobs: AlertFormJob[]
  /** An event alert's job; the page owns it so its hook fetches that job's pipelines. */
  job: string
  onJobChange: (job: string) => void
  /** The event an event alert watches ('' until one is picked). */
  eventType: AlertEventType | ''
  onEventTypeChange: (eventType: AlertEventType | '') => void
  /** The job's AutoCoder pipelines, for an import-batch event alert. */
  pipelines: AlertFormPipeline[]
  pipelinesLoading: boolean
  /** Who may be named for what has been typed in "Also notify". */
  recipientOptions: AlertRecipient[]
  recipientsLoading: boolean
  recipientsError: string | null
  recipientQuery: string
  onRecipientQueryChange: (q: string) => void
  /**
   * Set when the caller does not own the alert: why its event and recipients
   * are disabled, and why it cannot be changed with the agent (worded by
   * utils/permissionReason.ts). Name and schedule stay editable.
   */
  lockedReason?: string
  submitting: boolean
  /** The server's reason the last create or save failed. */
  error?: string | null
  /** "Watch an event" only. */
  onCreate?: (body: CreateAlertBody) => void
  onSave: (body: UpdateAlertBody) => void
  onClose: () => void
  /** Edit of a table or question alert: open the start screen with its card filled in. */
  onChangeWithAgent?: () => void
}

/**
 * "Watch an event" and Edit alert. A table or question alert is made, and
 * its tables, question and condition changed, only on the agent start screen
 * (the alert card): this dialog shows those read-only with "Change with the
 * agent", so one place writes checks. Here go the fields that cost no tokens:
 * name, schedule, recipients, and an event alert's event. An event alert is
 * saved active and never opens a chat.
 */
const AlertFormDialog: React.FC<AlertFormDialogProps> = ({
  open,
  alert,
  jobs,
  job,
  onJobChange,
  eventType,
  onEventTypeChange,
  pipelines,
  pipelinesLoading,
  recipientOptions,
  recipientsLoading,
  recipientsError,
  recipientQuery,
  onRecipientQueryChange,
  lockedReason,
  submitting,
  error,
  onCreate,
  onSave,
  onClose,
  onChangeWithAgent,
}) => {
  const editing = Boolean(alert)
  const source = alertSourceOf(alert)
  const kind = editing ? source.kind : 'event'
  const isEvent = kind === 'event'
  const originalSchedule = alert?.config?.schedule
  const originalRecipients = alert?.recipients ?? []
  const originalEventType = source.event?.type ?? ''
  const originalPipeline = source.event?.pipeline_id ?? ''

  const [name, setName] = useState(alert?.name ?? '')
  const [pipelineId, setPipelineId] = useState(originalPipeline)
  const [schedule, setSchedule] = useState<AlertSchedule>(originalSchedule ? scheduleBody(originalSchedule) : defaultAlertSchedule())
  const [recipients, setRecipients] = useState<string[]>(originalRecipients)

  const batchEvent = isEvent && isBatchEvent(eventType)

  const jobChanged = editing && isEvent && job !== source.job
  const eventChanged = editing && isEvent && (eventType !== originalEventType || (batchEvent ? pipelineId : '') !== originalPipeline)
  const sourceChanged = jobChanged || eventChanged
  const nameChanged = editing && name.trim() !== (alert?.name ?? '').trim()
  const scheduleChanged = editing && !isEvent && !sameSchedule(schedule, originalSchedule)
  const recipientsChanged = editing && !sameEmails(recipients, originalRecipients)
  const anyChange = sourceChanged || nameChanged || scheduleChanged || recipientsChanged

  const valid = Boolean(name.trim()) && (!isEvent || Boolean(job && eventType))
  const canSubmit = valid && !submitting && (editing ? anyChange : true)

  const handleJobSelect = (next: string) => {
    if (next === job) return
    setPipelineId('')
    onJobChange(next)
  }

  const handleEventSelect = (next: AlertEventType | '') => {
    if (!isBatchEvent(next)) setPipelineId('')
    onEventTypeChange(next)
  }

  const buildSource = (): AlertSource => ({
    kind: 'event',
    job,
    event: { type: eventType as AlertEventType, ...(batchEvent && pipelineId ? { pipeline_id: pipelineId } : {}) },
  })

  const handleSubmit = () => {
    if (!canSubmit) return
    if (!editing) {
      onCreate?.({ name: name.trim(), source: buildSource(), ...(recipients.length ? { recipients } : {}) })
      return
    }
    // Only what changed: a Manager editing a colleague's alert must never
    // send the source or recipients (the server refuses them).
    onSave({
      ...(nameChanged ? { name: name.trim() } : {}),
      ...(scheduleChanged ? { schedule: scheduleBody(schedule) } : {}),
      ...(sourceChanged ? { source: buildSource() } : {}),
      ...(recipientsChanged ? { recipients } : {}),
    })
  }

  const jobLabel = (n: string) => jobs.find((j) => j.name === n)?.displayName || n
  const kindLabel = ALERT_SOURCE_KINDS.find((k) => k.value === kind)?.label ?? ''
  // An edited alert's pipeline may no longer be listed (archived, renamed):
  // keep it selectable so the Select never holds an unknown value.
  const pipelineOptions = pipelineId && !pipelines.some((p) => p.id === pipelineId)
    ? [...pipelines, { id: pipelineId, name: pipelineId }]
    : pipelines
  const ruleText = alert?.config?.natural_language?.rule_text ?? ''
  const ruleContext = alert?.config?.natural_language?.context ?? ''

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit alert' : 'Watch an event'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {editing ? (
          <Box>
            <Typography variant="body2">
              Watches: <strong>{kindLabel}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">{KIND_FIXED_LINE}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">{SAVED_ACTIVE_LINE}</Typography>
        )}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          size="small"
          autoFocus
        />

        {!isEvent && alert && (
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }} aria-label="What it watches">
            <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Job:</Box> {jobLabel(source.job)}</Typography>
            {kind === 'table' ? (
              <Box>
                <Typography variant="body2" color="text.secondary">{alert.tables.length === 1 ? 'Table' : 'Tables'}</Typography>
                {alert.tables.length === 0 ? (
                  <Typography variant="body2">None yet</Typography>
                ) : alert.tables.map((t) => (
                  <Typography key={tablePath(t)} variant="body2">
                    {t.display_name || t.filename} <Box component="span" sx={{ color: 'text.secondary' }}>({tablePath(t)})</Box>
                  </Typography>
                ))}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary">Question</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{source.query?.question || '—'}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="body2" color="text.secondary">Condition</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ruleText || '—'}</Typography>
            </Box>
            {ruleContext && (
              <Box>
                <Typography variant="body2" color="text.secondary">Context</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ruleContext}</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">{CHANGE_WITH_AGENT_LINE}</Typography>
            <Box>
              <DisabledReason reason={lockedReason}>
                <Button size="small" variant="outlined" color="info" startIcon={<SmartToyIcon fontSize="small" />} onClick={onChangeWithAgent} disabled={submitting}>
                  Change with the agent
                </Button>
              </DisabledReason>
            </Box>
          </Paper>
        )}

        {isEvent && (
          <>
            <DisabledReason reason={lockedReason}>
              <FormControl size="small" fullWidth required>
                <InputLabel id="alert-form-job-label">Job</InputLabel>
                <Select
                  labelId="alert-form-job-label"
                  label="Job"
                  value={jobs.some((j) => j.name === job) ? job : ''}
                  onChange={(e) => handleJobSelect(String(e.target.value))}
                  inputProps={{ 'aria-label': 'Job' }}
                >
                  {jobs.map((j) => (
                    <MenuItem key={j.name} value={j.name}>{j.displayName || j.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DisabledReason>

            <DisabledReason reason={lockedReason}>
              <FormControl size="small" fullWidth required>
                <InputLabel id="alert-form-event-label">Event</InputLabel>
                <Select
                  labelId="alert-form-event-label"
                  label="Event"
                  value={eventType}
                  onChange={(e) => handleEventSelect(e.target.value as AlertEventType | '')}
                  inputProps={{ 'aria-label': 'Event' }}
                >
                  {ALERT_EVENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DisabledReason>
          </>
        )}

        {batchEvent && (
          <DisabledReason reason={lockedReason}>
            <FormControl size="small" fullWidth disabled={pipelinesLoading}>
              <InputLabel id="alert-form-pipeline-label" shrink>Pipeline</InputLabel>
              <Select
                labelId="alert-form-pipeline-label"
                label="Pipeline"
                notched
                displayEmpty
                value={pipelineId}
                onChange={(e) => setPipelineId(String(e.target.value))}
                inputProps={{ 'aria-label': 'Pipeline' }}
              >
                <MenuItem value="">Any pipeline</MenuItem>
                {pipelineOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DisabledReason>
        )}

        {!isEvent && <AlertSchedulePicker value={schedule} onChange={setSchedule} disabled={submitting} />}

        <DisabledReason reason={lockedReason}>
          <AlertRecipientsField
            value={recipients}
            onChange={setRecipients}
            options={recipientOptions}
            loading={recipientsLoading}
            error={recipientsError}
            inputValue={recipientQuery}
            onInputChange={onRecipientQueryChange}
          />
        </DisabledReason>

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="info">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {editing ? 'Save' : 'Create alert'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AlertFormDialog
